import { useCallback, useRef } from 'react';
import type { CancelableAxiosError } from '~/interfaces/api';

export const useRequestApi = <P, R>(
  requestFn: (params: P) => Promise<R>,
  onCommonSuccess?: (res: R) => void,
  onCommonError: (e?: CancelableAxiosError) => void = () => {
    console.error('エラーが発生しました');
  },
) => {
  // 多重実行防止フラグ。state ではなく ref で管理し、不要な re-render を起こさない
  const isApiActivatingRef = useRef(false);

  // 引数のコールバックは毎レンダー新しい参照になりうるため、ref で最新値を保持して
  // useCallback の依存配列を空にし、戻り値の関数参照を完全に安定化する
  const requestFnRef = useRef(requestFn);
  const onCommonSuccessRef = useRef(onCommonSuccess);
  const onCommonErrorRef = useRef(onCommonError);
  requestFnRef.current = requestFn;
  onCommonSuccessRef.current = onCommonSuccess;
  onCommonErrorRef.current = onCommonError;

  return useCallback(
    async (
      requestParams: P,
      onSuccess?: (res: R) => void | Promise<void>,
      onError?: (err?: CancelableAxiosError) => void | Promise<void>,
      onFinally?: () => void,
    ) => {
      if (isApiActivatingRef.current) return;

      isApiActivatingRef.current = true;

      try {
        const res = await requestFnRef.current(requestParams);
        onCommonSuccessRef.current && onCommonSuccessRef.current(res);
        await (onSuccess && onSuccess(res));
        return res;
      } catch (e: any) {
        onCommonErrorRef.current(e);
        await (onError && onError(e));
      } finally {
        isApiActivatingRef.current = false;
        onFinally && onFinally();
      }
    },
    [],
  );
};
